import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Award, MapPin, Star, Heart, X, ZoomIn } from 'lucide-react';
import FadeCarousel from './FadeCarousel';

const BenefitsSection = () => {
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const benefits = [
    {
      icon: Award,
      title: '創業80年の実績',
      description: '代々受け継がれてきた技術と、地域への責任感を持って対応します。',
    },
    {
      icon: MapPin,
      title: '柔軟なフットワーク',
      description:
        '新潟店・長野店を中心に、半径100km商圏で墓じまい・墓石解体に対応。「少し遠いから迷っていた」という方にも、安心してご相談いただけます。',
    },
    {
      icon: Star,
      title: 'Googleマップでの高評価・多くの口コミ',
      description:
        '実際にご依頼いただいたお客様から、「丁寧に説明してくれた」「安心して任せられた」との声を多数いただいています。口コミの数と評価は、私たちの仕事ぶりを客観的に示す、何よりの証拠だと考えています。',
    },
    {
      icon: Heart,
      title: '墓じまい＋手元供養・モニュメントまでのトータル提案',
      description:
        'ただ「更地にして終わり」ではなく、墓石を手元供養の骨壺や想いを永遠に残すデジタルメモリアルのご提案も可能です。',
    },
  ];

  return (
    <section id="benefits" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-stone-50 to-stone-100">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-6 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-4">
            選ばれる理由
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-800 mb-4">
            創業80年、口コミ信頼高評価
          </h2>
          <p className="text-xl text-stone-600">
            地域で選ばれ続ける墓じまいのパートナー
          </p>
        </motion.div>

        {/* Image Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16 rounded-2xl overflow-hidden shadow-2xl"
        >
          <img 
            src="images/86.png" 
            alt="創業80年、口コミ高評価" 
            className="w-full h-auto object-cover"
          />
        </motion.div>

        {/* Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mb-12"
        >
          <h3 className="text-2xl font-bold text-stone-800 mb-4">
            「地域の事情」と「ご家族の想い」を知り尽くした私達だからできる、心に寄り添う墓じまいのご提案
          </h3>
          <p className="text-stone-600 max-w-3xl mx-auto leading-relaxed text-left">
            私たちは、新潟県・長野県を中心に、80年以上にわたり墓石の建立・墓じまいに携わってきました。
            長い年月のなかで、地域の風土や雪の状況、市営霊園の特徴、寺院や永代供養塔とのお付き合いなど、さまざまな経験を積んできました。
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-stone-200"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-amber-700" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-stone-800 mb-3">
                      {benefit.title}
                    </h4>
                    <p className="text-stone-600 leading-relaxed">
                      {benefit.description}
                    </p>
                    {index === 0 && (
                      <div className="mt-6 bg-amber-50 p-6 rounded-xl border border-amber-200">
                        <ul className="space-y-4">
                          <li className="flex flex-col">
                            <span className="font-bold text-amber-900 text-lg border-b-2 border-amber-200 pb-1 mb-1 inline-block w-full">
                              一級技能士(石材施工)
                            </span>
                            <span className="text-sm text-stone-600 pl-2">
                              ⇒業界唯一の国家資格取得者が在籍
                            </span>
                          </li>
                          <li className="flex flex-col">
                            <span className="font-bold text-amber-900 text-lg border-b-2 border-amber-200 pb-1 mb-1 inline-block w-full">
                              石匠位-経産省認定
                            </span>
                          </li>
                          <li className="flex flex-col">
                            <span className="font-bold text-amber-900 text-lg border-b-2 border-amber-200 pb-1 mb-1 inline-block w-full">
                              日本墓石名店100選認定店
                            </span>
                            <span className="text-sm text-stone-600 pl-2">
                              ⇒全国二万社以上の墓石店の中から厳正な審査をもとに推薦された認定店です
                            </span>
                          </li>
                          <li className="flex flex-col">
                            <span className="font-bold text-amber-900 text-lg border-b-2 border-amber-200 pb-1 mb-1 inline-block w-full">
                              ガイドライン遵守認定店
                            </span>
                            <span className="text-sm text-stone-600 pl-2">
                              ⇒日本産業石材協会認定店
                            </span>
                          </li>
                        </ul>
                      </div>
                    )}
                    {index === 1 && (
                      <div className="mt-6 rounded-xl overflow-hidden shadow-md">
                        <img 
                          src="images/91.png" 
                          alt="柔軟なフットワーク" 
                          className="w-full h-auto object-cover"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 text-center bg-gradient-to-r from-amber-50 to-stone-50 rounded-2xl p-8 border border-amber-200"
        >
          <p className="text-lg text-stone-700 leading-relaxed">
            「どのような形で先祖の想いを残すのがよいか」私たちも一緒に考えさせて頂きます。
            <br />
            形式にとらわれず、ご家族らしい供養の形を徹底サポート致します。
          </p>
        </motion.div>
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {modalImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalImage(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 cursor-zoom-out"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setModalImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-stone-300 transition-colors p-2"
                aria-label="閉じる"
              >
                <X className="w-8 h-8" />
              </button>
              <img
                src={modalImage}
                alt="創業80年の実績（拡大）"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default BenefitsSection;
